// 新聞頁面 一則評論元件
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import Image from 'next/image';
import _ from 'lodash';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/zh-cn';
import { DEFAULT_USER_IMG } from '@/common/constant';
import newsClient from '@/common/apiClient/newsClient';

import styles from '../../app/[locale]/[category]/page.module.scss';

dayjs.extend(relativeTime);

const NewsCommentComponent = ({ info, newsId, user }) => {
    const params = useParams();
    const { locale } = params;

    const comment = info?.comment; //留言內容
    const name = info?.nickName; // 留言使用者名稱

    // 語言設定
    dayjs.locale(`zh-${locale}`);
    // 留言時間
    const commentTime = dayjs(info?.createdTime).fromNow();

    // 是否按讚
    const [isThumb, setIsThumb] = useState(info?.isThumbed);
    // 讚數
    const [thumbAmount, setThumbAmount] = useState(info?.thumb);
    // 頭像框
    const [frame, setFrame] = useState(info?.avatarFrame);

    // 按讚、收回
    const onThumbUp = useCallback(async () => {
        try {
            const result = await newsClient.postCommentThumb(newsId, info?.id);
            if (result.message === 'success') {
                setThumbAmount((prev) => prev + (isThumb ? -1 : 1));
                setIsThumb((prev) => !prev);
            }
        } catch (e) {
            console.error(e);
        }
    }, [newsId, info?.id, isThumb]);

    // 處理頭像框
    const handelFrame = () => {
        if (user?.id === info?.userId) setFrame(user?.avatarFrame);
    };

    // 防連點 .3s
    const thumbDebounce = useMemo(() => _.debounce(onThumbUp, 300), [onThumbUp]);

    useEffect(() => {
        return () => {
            thumbDebounce.cancel();
        };
    }, [thumbDebounce]);

    useEffect(() => {
        if (user?.id) handelFrame();
    }, [user]);

    return (
        <div className="relative w-full py-[22px] border-t-1 border-dashed !border-[rgba(114,114,114,.5)]">
            <div className="flex gap-x-[18px] items-center flex-grow ulg:flex-col ulg:items-start ulg:gap-y-[18px]">
                <div className="ulg:flex ulg:items-center ulg:gap-x-[16px]">
                    <div className="img-box w-[60px] aspect-[1] !overflow-visible flex-shrink-0">
                        {/* 如果有頭像框 */}
                        {frame && (
                            <Image
                                className="!z-[2] set-center !object-contain !h-[110%] !w-[110%] !max-w-[unset]"
                                width={60}
                                height={60}
                                src={frame}
                                alt="frame"
                                sizes="60px"
                            />
                        )}
                        {/* 頭像 */}
                        <Image
                            className={`${
                                frame ? 'set-center !w-[95%] !h-[95%]' : ''
                            } rounded-[50%]`}
                            width={60}
                            height={60}
                            src={info?.image || DEFAULT_USER_IMG}
                            alt="thumbnail"
                        />
                    </div>

                    {/* 留言者名稱 */}
                    <div className="hidden ulg:block">
                        <p className="text-btn3b text-white line-clamp-1">{name}</p>
                    </div>
                </div>

                <div className="flex justify-between gap-x-[20px] ulg:flex-col ulg:gap-y-[18px] flex-grow">
                    <div className="space-y-[15px] ulg:space-y-0">
                        {/* 留言者名稱 */}
                        <p className="text-btn3b text-white line-clamp-1 ulg:hidden">{name}</p>

                        {/* 留言內容 */}
                        <p className="text-btn4 text-white break-all">{comment}</p>
                    </div>

                    <div className="space-y-[24px] text-btn2 flex-shrink-0 relative top-[2px] ulg:flex ulg:space-y-0 ulg:items-center ulg:top-0 ulg:gap-x-[20px]">
                        {/* 留言時間 */}
                        <p className="text-black4 flex-shrink-0">{commentTime}</p>

                        {/* 讚數 */}
                        <button
                            onClick={thumbDebounce}
                            className="flex items-center gap-x-[8px] w-full justify-end ulg:justify-start"
                        >
                            <Image
                                className={
                                    isThumb ? styles.bounce : 'grayscale-[2] brightness-[150%]'
                                }
                                width={16}
                                height={16}
                                src="/images/icon/icon_bou_thumb.svg"
                                alt="icon"
                            />
                            <span className={isThumb ? 'text-bou' : 'text-purple1'}>
                                {thumbAmount}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <hr id={`news-comment-${info?.id}`} className="absolute top-[-40px] opacity-0 z-[-1]" />
        </div>
    );
};

export default NewsCommentComponent;
